import { eachDayOfInterval, intervalToDuration, addDays, startOfYear, lastDayOfYear, eachYearOfInterval, startOfMonth, lastDayOfMonth, eachMonthOfInterval, startOfQuarter, lastDayOfQuarter, eachQuarterOfInterval, subMonths, isSameDay, getYear, format } from "date-fns";
import * as _ from "lodash";
import { IElectricityMeter, INaturalGasMeter } from "./EnergyMeter";
import { IElectricityOmipPricesPTHistory } from "./OmipPrice";


export class ContractPrice {
    id: string;
    start: Date;
    end: Date;
    name: string;
    omipHistory: IElectricityOmipPricesPTHistory[] = [];
    history: ContractPriceHistory[];
    
    electricityPriceValues: ElectricityPriceValues[] = [];
    naturalGasPriceValues: NaturalGasPriceValues[] = [];
    
    priceType: 'years' | 'quarters' | 'months';
    priceTypeValue: number;
    timeline: string;

    forGas: boolean = false;
    forElectricity: boolean = true;

    chartColor?: string = undefined

    constructor(
        id: string,
        start: Date,
        end: Date,
        name: string    
    ) {
        this.id = id;
        this.start = start;
        this.end = end;
        this.name = name;

        let currentDate = new Date(new Date().setHours(0, 0, 0, 0));
        let minDate = subMonths(currentDate, 1);
        
        this.history = eachDayOfInterval({start: minDate, end: currentDate}).map( (date) => new ContractPriceHistory(date, +_.random(60, 80, true).toFixed(2) ));

        let intervalDuration = intervalToDuration({start: start, end: end});

        if (isSameDay(startOfYear(start), start) && isSameDay(lastDayOfYear(end), end)) {
            this.priceType = 'years'
            this.priceTypeValue = eachYearOfInterval({start: start, end: end}).length;
            
            if (this.priceTypeValue == 1) {
                this.timeline = getYear(start).toString()
            } else {
                this.timeline = getYear(start) + ' - ' + getYear(end);
            }
        } else if (isSameDay(startOfQuarter(start), start) && isSameDay(lastDayOfQuarter(end), end)) {
            this.priceType = 'quarters';
            this.priceTypeValue = eachQuarterOfInterval({start: start, end: end}).length;
            
            if (this.priceTypeValue == 1) {
                this.timeline = format(start, 'LLL') + ' ' + getYear(start) + ' - ' + format(end, 'LLL') + ' ' + getYear(end);
            } else {
                this.timeline = format(start, 'LLL') + ' ' + getYear(start) + ' - ' + format(end, 'LLL') + ' ' + getYear(end);
            }
        } else if (isSameDay(startOfMonth(start), start) && isSameDay(lastDayOfMonth(end), end)) {
            this.priceType = 'months'
            this.priceTypeValue = eachMonthOfInterval({start: start, end: end}).length;

            if (this.priceTypeValue == 1) {
                this.timeline = format(start, 'LLL') + ' ' + getYear(start);
            } else {
                this.timeline = format(start, 'LLL') + ' ' + getYear(start) + ' - ' + format(end, 'LLL') + ' ' + getYear(end);
            }
        } else {
            this.priceType = 'months';
            this.priceTypeValue = eachMonthOfInterval({start: start, end: end}).length;

            if (this.priceTypeValue == 1) {
                this.timeline = format(start, 'LLL') + ' ' + getYear(start);
            } else {
                this.timeline = format(start, 'LLL') + ' ' + getYear(start) + ' - ' + format(end, 'LLL') + ' ' + getYear(end);
            }
        }
    }

    get currentValue(): ContractPriceHistory {
        return this.history.at(-1) ?? new ContractPriceHistory(new Date(0));
    }
}

export class ContractPriceHistory {
    date: Date;
    basePrice: number;

    forGas: boolean = false;
    forElectricity: boolean = true;

    constructor(
        date: Date, 
        basePrice: number = 0,
    ) {
        this.date = date;
        this.basePrice = basePrice;
    }
    [key: string]: any;
}

export class ElectricityPriceValues {
    peak: number;
    standard: number;
    offPeak: number;
    superOffPeak: number;

    tensionLevel?: string;
    countingCycle?: string;
    timeCycle?: string;

    contractedPowerFrom?: number;
    contractedPowerTo?: number;

    constructor(
        peak: number,
        offPeak: number,
        standard: number,
        superOffPeak: number,

        tensionLevel?: string,
        countingCycle?: string,
        timeCycle?: string,

        contractedPowerFrom?: number,
        contractedPowerTo?: number
    ) {
        this.peak = peak;
        this.offPeak = offPeak;
        this.standard = standard;
        this.superOffPeak = superOffPeak;

        this.tensionLevel = tensionLevel;
        this.countingCycle = countingCycle;
        this.timeCycle = timeCycle;

        this.contractedPowerFrom = contractedPowerFrom;
        this.contractedPowerTo = contractedPowerTo;
    }
}

export class NaturalGasPriceValues {
    gas: number;
    
    dailyPrices: {
        monthInterval: [number, number],
        price: number
    }[] = [];

    pressureLevel?: string;
    tier?: string;
    tariff?: string;

    annualConsumptionFrom?: number;
    annualConsumptionTo?: number;

    constructor(
        gas: number,
        pressureLevel?: string,
        tier?: string,
        tariff?: string,
        annualConsumptionFrom?: number,
        annualConsumptionTo?: number
    ) {
        this.gas = gas;

        this.pressureLevel = pressureLevel;
        this.tier = tier;

        this.annualConsumptionFrom = annualConsumptionFrom;
        this.annualConsumptionTo = annualConsumptionTo;
    }
}

export namespace GroupingSimulation {
    export interface IEquipment {
        id?: number
        guid?: string
        code: string

        graphColor?: string

        priceData: IFixedPriceSimulation[]
        consumptions: IEquipmentConsumption[]
    }

    export class ElectricityEquipment implements IEquipment {
        id?: number;
        guid?: string;
        code: string;

        tensionLevelId: string;
        tensionLevelDescription?: string

        power: number;

        countingCycleId?: string;
        countingCycleDescription?: string;
        
        timeCycleId?: string;
        timeCycleDescription?: string;

        graphColor?: string;

        priceData: ElectricityFixedPriceSimulation[];
        consumptions: ElectricityEquipmentConsumption[];

        constructor( id: number | undefined, guid: string | undefined, code: string, tensionLevelId: string, power: number, countingCycleId?: string, timeCycleId?: string, consumptions?: ElectricityEquipmentConsumption[] ) {
            this.id = id;
            this.guid = guid;
            this.code = code;
            this.tensionLevelId = tensionLevelId;
            this.power = power; 
            this.countingCycleId = countingCycleId; 
            this.timeCycleId = timeCycleId; 
            
            this.priceData = [];
            this.consumptions = consumptions ?? [];
        }

        
    }

    export class NaturalGasEquipment implements IEquipment {
        id?: number;
        guid?: string;
        code: string;
        
        pressureLevelId: string;
        pressureLevelDescription?: string;

        tariffId?: string;
        tariffDescription?: string;

        tierId?: string;
        tierDescription?: string;

        graphColor?: string;

        priceData: NaturalGasFixedPriceSimulation[];
        consumptions: NaturalGasEquipmentConsumption[];

        constructor( id: number | undefined, guid: string | undefined, code: string, pressureLevelId: string, tariffId?: string, tierId?: string, consumptions?: NaturalGasEquipmentConsumption[] ) {
            this.id = id;
            this.guid = guid;
            this.code = code;
            this.pressureLevelId = pressureLevelId;
            this.tariffId = tariffId;
            this.tierId = tierId;

            this.priceData = [];
            this.consumptions = consumptions ?? [];
        }
    }

    export interface IEquipmentConsumption {
        date: Date;

        get totalValue(): number;
        [key: string]: any;
    }

    export class ElectricityEquipmentConsumption implements IEquipmentConsumption {
        date: Date;
        peak: number;
        standard: number;
        offPeak: number;
        superOffPeak: number;

        constructor( date: Date, peak?: number, standard?: number, offPeak?: number, superOffPeak?: number) {
            if (date instanceof Date) {
                this.date = date;
            } else {
                this.date = new Date(date);
            }
            
            this.peak = peak ?? 0;
            this.standard = standard ?? 0;
            this.offPeak = offPeak ?? 0;
            this.superOffPeak = superOffPeak ?? 0;
        }

        get totalValue(): number {
            return this.peak + this.standard + this.offPeak + this.superOffPeak;
        }
        [key: string]: any;
    }

    export class NaturalGasEquipmentConsumption implements IEquipmentConsumption {
        date: Date;
        consumption: number;

        constructor(date: Date, consumption?: number) {
            if (date instanceof Date) {
                this.date = date;
            } else {
                this.date = new Date(date);
            }
            
            this.consumption = consumption ?? 0;
        }

        get totalValue(): number {
            return this.consumption;
        }
        [key: string]: any;
    }

    export interface IFixedPriceSimulation {
        priceId: string;
        fixedPricePercentage: number;
        start: Date;
        end: Date;
    }

    export class ElectricityFixedPriceSimulation implements IFixedPriceSimulation {
        priceId: string;
        fixedPricePercentage: number;
        start: Date;
        end: Date;

        peakPrice: number;
        standardPrice: number;
        offPeakPrice: number;
        superOffPeakPrice: number;

        constructor(priceId: string, fixedPricePercentage: number, start: Date, end: Date, peakPrice?: number, standardPrice?: number, offPeakPrice?: number, superOffPeakPrice?: number) {
            this.priceId = priceId;
            this.fixedPricePercentage = fixedPricePercentage ?? 0;
            this.start = start;
            this.end = end;

            this.peakPrice = peakPrice ?? 0;
            this.standardPrice = standardPrice ?? 0;
            this.offPeakPrice = offPeakPrice ?? 0;
            this.superOffPeakPrice = superOffPeakPrice ?? 0;
        }
    }

    export class NaturalGasFixedPriceSimulation implements IFixedPriceSimulation {
        priceId: string;
        fixedPricePercentage: number;
        start: Date;
        end: Date;

        price: number;

        constructor(priceId: string, fixedPricePercentage: number, start: Date, end: Date, price?: number) {
            this.priceId = priceId;
            this.fixedPricePercentage = fixedPricePercentage ?? 0;
            this.start = start;
            this.end = end;

            this.price = price ?? 0;
        }
    }

    export class PriceSummary {
        priceId: string
        priceName: string
        priceInterval: Interval
        
        equipments: EquipmentSummary[] = []
        childPrices: string[] = []

        constructor(
            priceId: string,
            priceName: string,
            priceInterval: Interval
        ) {
            this.priceId = priceId;
            this.priceName = priceName;
            this.priceInterval = priceInterval;
        }
    }

    export class EquipmentSummary {
        equipmentId: string
        equipmentCode: string
        coverPercentage: number

        constructor(
            equipmentId: string,
            equipmentCode: string,
            coverPercentage: number
        ) {
            this.equipmentId = equipmentId;
            this.equipmentCode = equipmentCode;
            this.coverPercentage = coverPercentage;
        }
    }
}
